import { useMemo } from "react";
import { useMessageInputState } from "../../../../App/State/Hooks/MessageDialog/useMessageInput";
import { useMessageScheduleState } from "../../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useAppSelector } from "../../../../App/State/Hooks/reduxHooks";
import { selectSelectedCandidatesByType } from "../../../../App/State/Slices/MessageDialogSlice";
import { useUserProfileStore } from "../../../../App/State/Stores/UserProfileStore";
import {
  AreaOfInterestPattern,
  AreaOfInterestTag,
  RecruiterTitlePattern,
} from "../../../FunctionMethods/MessageFunctions";

const Error_Message_Recipients = "No valid recipients selected";
const Error_Message_RecruiterTitle = "No Recruiter Title available, please update profile";
const Error_Message_AreaOfInterest = `The ${AreaOfInterestTag} tag is only allowed in form welcome messages. Please remove it to proceed.`;

export type ValidateTagsAndRecipientsResult = ReturnType<typeof useValidateTagsAndRecipients>;

export function useValidateTagsAndRecipients(
  defaultMessage: string,
  defaultScheduleMessage: string,
  isGroup = false,
  isForm = false,
) {
  const { messageType, body, subject } = useMessageInputState();
  const { msgListQueue } = useMessageScheduleState();
  const isEmail = messageType === "email";
  const selectedTextCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "text"));
  const selectedEmailCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "email"));
  const validRecipients = isEmail ? selectedEmailCandidates : selectedTextCandidates;
  const hasValidRecipients = isGroup || validRecipients.length > 0;
  const { hasValidTitle } = useUserProfileStore();

  const tagAnalysis = useMemo(() => {
    const result = {
      hasTitleInBody: RecruiterTitlePattern.test(body),
      hasTitleInSubject: subject ? RecruiterTitlePattern.test(subject) : false,
      hasAreaOfInterestInBody: AreaOfInterestPattern.test(body),
      hasAreaOfInterestInSubject: subject ? AreaOfInterestPattern.test(subject) : false,
      hasQueuedMessageWithTitle: false,
      hasQueuedMessageWithAreaOfInterest: false,
    };

    for (const msg of msgListQueue) {
      if (!result.hasQueuedMessageWithTitle) {
        result.hasQueuedMessageWithTitle =
          RecruiterTitlePattern.test(msg.bodyRaw) || RecruiterTitlePattern.test(msg.title);
      }
      if (!result.hasQueuedMessageWithAreaOfInterest) {
        result.hasQueuedMessageWithAreaOfInterest =
          AreaOfInterestPattern.test(msg.bodyRaw) || AreaOfInterestPattern.test(msg.title);
      }
      if (result.hasQueuedMessageWithTitle && result.hasQueuedMessageWithAreaOfInterest) {
        break;
      }
    }

    return result;
  }, [body, subject, msgListQueue.length]);

  const {
    hasTitleInBody,
    hasTitleInSubject,
    hasAreaOfInterestInBody,
    hasAreaOfInterestInSubject,
    hasQueuedMessageWithTitle,
    hasQueuedMessageWithAreaOfInterest,
  } = tagAnalysis;

  const hasInputWithTitle = hasTitleInBody || hasTitleInSubject;
  const hasInputWithAreaOfInterest = hasAreaOfInterestInBody || hasAreaOfInterestInSubject;

  const hasTitleError = useMemo(
    () => !hasValidTitle() && (hasInputWithTitle || hasQueuedMessageWithTitle),
    [hasInputWithTitle, hasQueuedMessageWithTitle, hasValidTitle],
  );

  const hasAreaOfInterestError = useMemo(() => {
    const hasTags = hasInputWithAreaOfInterest || hasQueuedMessageWithAreaOfInterest;
    return hasTags && (!isEmail || !isForm);
  }, [hasInputWithAreaOfInterest, hasQueuedMessageWithAreaOfInterest, isEmail, isForm]);

  const disabledTooltipText = hasValidRecipients
    ? hasTitleError
      ? Error_Message_RecruiterTitle
      : hasAreaOfInterestError
        ? Error_Message_AreaOfInterest
        : defaultMessage
    : Error_Message_Recipients;
  const disabledScheduleMessagesTooltip = hasValidRecipients
    ? hasTitleError
      ? Error_Message_RecruiterTitle
      : hasAreaOfInterestError
        ? Error_Message_AreaOfInterest
        : defaultScheduleMessage
    : Error_Message_Recipients;

  return {
    disabledTooltipText,
    disabledScheduleMessagesTooltip,
    hasValidRecipients,
    hasTitleError,
    hasAreaOfInterestError,
    hasTitleInBody,
    hasTitleInSubject,
    hasAreaOfInterestInBody,
    hasAreaOfInterestInSubject,
    recruiterTitleError: Error_Message_RecruiterTitle,
    areaOfInterestError: Error_Message_AreaOfInterest,
  } as const;
}
